/**
 * useVerifyArtifact — public, unauthenticated fetch for GET /v1/verify/:token.
 *
 * No auth header. No Firebase dependency. The endpoint always returns HTTP 200;
 * the `status` field carries the verdict ('verified' | 'notVerified' | 'expired').
 *
 * Uses TanStack Query (server-state, not Firestore). A 429 RATE_LIMITED response
 * is surfaced as an Error so the UI can render a friendly rate-limit message.
 *
 * Capacitor readiness:
 *   - No firebase/* imports — this is a plain fetch.
 *   - No navigator.onLine — uses useOnlineStatus() in the consuming page if needed.
 *   - No localStorage/sessionStorage — no caching of sensitive data.
 */

import { env } from '@/lib/env';
import type { VerifyArtifactResponse } from '@/types/api';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** TanStack Query key factory for artifact verification. */
export const verifyArtifactKeys = {
  forToken: (token: string) => ['verifyArtifact', token] as const,
};

export interface UseVerifyArtifactResult {
  data: VerifyArtifactResponse | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetch the public verify verdict for a token.
 *
 * @param token - The verify token from the URL (e.g. `/verify/:token`). Pass
 *                null/undefined to skip fetching.
 */
export function useVerifyArtifact(token: string | null | undefined): UseVerifyArtifactResult {
  const result: UseQueryResult<VerifyArtifactResponse, Error> = useQuery<
    VerifyArtifactResponse,
    Error
  >({
    queryKey: verifyArtifactKeys.forToken(token ?? ''),
    queryFn: async (): Promise<VerifyArtifactResponse> => {
      const response = await fetch(`${env.VITE_GG_API_BASE_URL}/v1/verify/${token}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        // No Authorization header — this is an unauthenticated public endpoint.
      });

      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      if (!response.ok) {
        throw new Error(`Unexpected response ${response.status}: ${response.statusText}`);
      }

      return (await response.json()) as VerifyArtifactResponse;
    },
    enabled: Boolean(token),
    // Verify results are stable — the artifact doesn't change. Cache for 5 min.
    staleTime: 5 * 60_000,
    // On transient failure, retry once with a short backoff before surfacing error.
    retry: 1,
    retryDelay: 1500,
  });

  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error ?? null,
  };
}
